/**
 * Maker House Metaobject Mega Menu
 *
 * Self-contained web component that drives:
 *  - desktop hover/click flyout with backdrop dismiss
 *  - mobile slide-in drawer styled like the default theme drawer, driven by a
 *    JSON tree (default: assets/menu-tree.json) with unlimited nesting depth
 *  - keyboard navigation (arrows, Home/End, Esc, Tab focus trap in drawer)
 *  - ARIA state sync (aria-expanded, aria-modal)
 *
 * The mobile drawer fetches its tree on first open. The fetch is cached in
 * memory for the life of the page and short-circuited via fetch's HTTP cache
 * for repeat visits, so the JSON file scales well past a few hundred items.
 *
 * No theme-internal dependencies — drop-in safe.
 */

class MakerHouseMegaMenu extends HTMLElement {
  constructor() {
    super();
    this._openItem = null;
    this._closeTimer = null;
    this._tree = null;
    this._treePromise = null;
    this._lastFocusedBeforeDrawer = null;
    this._panelStack = [];
    // Mobile drawer levels are built once and reused: the root level for every open, and each
    // submenu level (keyed by its menu node) for every drill-in.
    this._rootPanelEntry = null;
    this._panelCache = new WeakMap();
    // Desktop panels whose contents haven't been built yet, mapped to their menu node.
    this._unbuiltPanelNodes = new WeakMap();
    this._handleDocClick = this._handleDocClick.bind(this);
    this._handleKeydown = this._handleKeydown.bind(this);
    this._handleResize = this._handleResize.bind(this);
    this._handleAnalyticsClick = this._handleAnalyticsClick.bind(this);
  }

  connectedCallback() {
    this._backdrop = this.querySelector('[data-mh-mm-backdrop]');
    this._barList = this.querySelector('[data-mh-mm-bar]');
    this._mobileTrigger = this.querySelector('[data-mh-mm-mobile-trigger]');
    this._drawer = this.querySelector('[data-mh-mm-drawer]');
    this._drawerStack = this.querySelector('[data-mh-mm-drawer-stack]');
    this._drawerClose = this.querySelector('[data-mh-mm-drawer-close]');
    this._drawerBackdrop = this.querySelector('[data-mh-mm-drawer-backdrop]');
    this._drawerTitle = this.querySelector('[data-mh-mm-drawer-title]');
    this._defaultDrawerTitle = this._drawerTitle ? this._drawerTitle.textContent : '';
    this._barItems = [];
    this._panels = [];

    // Inline JSON tree (legacy fallback). The current Liquid path no longer
    // emits this, but supporting it keeps the component drop-in safe for
    // anyone who pre-renders the tree server-side.
    const treeScript = this.querySelector('[data-mh-mm-tree]');
    if (treeScript && treeScript.textContent.trim()) {
      try {
        const inline = JSON.parse(treeScript.textContent);
        this._tree = inline.items
          ? inline
          : { items: inline.children || [] };
      } catch (err) {
        console.warn('[mh-megamenu] failed to parse inline tree JSON', err);
      }
    }

    this._wireMobile();
    this._wireBackdrop();
    this._portalOverlays();
    this._syncHeaderMenuStyle();
    this._wireAnalytics();

    document.addEventListener('keydown', this._handleKeydown);
    window.addEventListener('resize', this._handleResize, { passive: true });

    // Eagerly fetch the tree so the desktop bar populates ASAP. Mobile
    // drawer reuses the same Promise.
    this._loadTree().then(() => {
      this._buildDesktopBar();
      this._wireDesktop();
      // Portal panels now that they exist; idempotent for drawer/backdrop.
      this._portalOverlays();
      this._measureBar();
      this._checkHeaderStack();
    });
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this._handleKeydown);
    document.removeEventListener('click', this._handleDocClick);
    document.removeEventListener('click', this._handleAnalyticsClick, true);
    window.removeEventListener('resize', this._handleResize);
    if (this._closeTimer) clearTimeout(this._closeTimer);
    // Clean up everything we portalled to <body>: drawer, backdrop, and
    // each desktop flyout panel.
    if (this._drawer && this._drawer.parentNode === document.body) {
      this._drawer.remove();
    }
    if (this._backdrop && this._backdrop.parentNode === document.body) {
      this._backdrop.remove();
    }
    (this._panels || []).forEach((panel) => {
      if (panel.parentNode === document.body) panel.remove();
    });
  }

  /**
   * The drawer, the desktop backdrop, and the desktop flyout panels must all
   * overlay the whole viewport, but the header is wrapped in a `contain:
   * layout` block which establishes a containing block for any
   * `position: fixed` descendant — and creates a stacking context that
   * traps z-index. Move the overlays to <body> so they:
   *   - escape the header's containing block (render edge-to-edge)
   *   - stack correctly relative to each other (panel z:100 > backdrop z:99)
   *
   * Panels are still wired to their bar items in JS (the DOM relationship
   * was only used by the open/close handlers, which use cached references
   * via this._openItem rather than DOM traversal).
   *
   * The CSS custom properties that drive their colours (--mh-mm-*) are
   * declared inline on this host element. Once an element leaves the host's
   * subtree it no longer inherits them, so we copy them onto each portalled
   * element's inline style before moving.
   */
  _portalOverlays() {
    const copyVars = (el) => {
      const hostStyle = this.style;
      for (let i = 0; i < hostStyle.length; i++) {
        const name = hostStyle[i];
        if (name.startsWith('--mh-mm-')) {
          el.style.setProperty(name, hostStyle.getPropertyValue(name));
        }
      }
    };

    const portal = (el) => {
      if (!el || el.parentNode === document.body) return;
      copyVars(el);
      document.body.appendChild(el);
    };

    portal(this._drawer);
    portal(this._backdrop);
    (this._panels || []).forEach(portal);
  }

  /* -- Desktop ------------------------------------------------------------ */

  /**
   * One-shot global handlers that don't depend on the bar items being
   * present. Called from connectedCallback before the JSON tree resolves.
   */
  _wireBackdrop() {
    if (this._backdrop) {
      this._backdrop.addEventListener('click', () => this._closePanel());
    }

    document.addEventListener('click', this._handleDocClick);

    // Arrow Left/Right/Home/End between top-level triggers
    this.addEventListener('keydown', (e) => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return;
      const triggers = this._barItems.map((i) => i.querySelector('[data-mh-mm-trigger]'));
      const current = triggers.indexOf(document.activeElement);
      if (current === -1) return;
      e.preventDefault();
      let next = current;
      if (e.key === 'ArrowRight') next = (current + 1) % triggers.length;
      if (e.key === 'ArrowLeft') next = (current - 1 + triggers.length) % triggers.length;
      if (e.key === 'Home') next = 0;
      if (e.key === 'End') next = triggers.length - 1;
      triggers[next].focus();
    });
  }

  /**
   * Wire pointer + keyboard handlers on the desktop bar items. Called once
   * after the bar is built from JSON.
   */
  _wireDesktop() {
    this._barItems = Array.from(this.querySelectorAll('[data-mh-mm-item]'));
    // Cache item -> panel and panel -> item before portalling, so the
    // open/close logic can find the panel when it's no longer a DOM
    // descendant of its bar item.
    this._panelByItem = new Map();
    this._itemByPanel = new Map();
    this._panels = [];
    this._barItems.forEach((item) => {
      const panel = item.querySelector('[data-mh-mm-panel]');
      if (!panel) return;
      this._panelByItem.set(item, panel);
      this._itemByPanel.set(panel, item);
      this._panels.push(panel);
    });

    this._barItems.forEach((item) => {
      const trigger = item.querySelector('[data-mh-mm-trigger]');
      const panel = this._panelByItem.get(item);
      if (!trigger || !panel) return;

      // Click toggles the panel. Pointer / mouse / keyboard / touch all
      // converge on click — no hover-to-open, so users can't trigger the
      // menu by accident while scanning past the bar.
      const onTriggerClick = (e) => {
        const href = trigger.getAttribute('href');
        if (this._openItem === item) {
          // Already open: second activation either navigates (if there's
          // a real href) or just closes.
          if (!href || href === '#') {
            e.preventDefault();
            this._closePanel();
          }
          return;
        }
        // Not open yet: prevent navigation, open the panel.
        if (href && href !== '#') e.preventDefault();
        this._openPanel(item);
      };

      trigger.addEventListener('click', onTriggerClick);

      // Panels open on click, so hovering or focusing the trigger is the cue to build the panel
      // and fetch its images.
      const prepare = () => this._preparePanel(panel);
      trigger.addEventListener('pointerenter', prepare, { once: true });
      trigger.addEventListener('focus', prepare, { once: true });

      // Keyboard: Enter / Space / ArrowDown opens. ArrowDown moves focus into
      // the panel. Esc inside the panel closes and returns focus.
      trigger.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this._openPanel(item);
          const firstLink = panel.querySelector('a, button');
          if (firstLink) firstLink.focus();
        }
      });

      panel.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          this._closePanel();
          trigger.focus();
        }
      });
    });
  }

  /**
   * Build the desktop top-level row + flyout panels from the loaded JSON
   * tree. Markup mirrors the previous Liquid output so the existing CSS in
   * mega-menu.css keeps working unchanged.
   */
  _buildDesktopBar() {
    if (!this._barList || !this._tree) return;

    const items = this._tree.items || [];

    // Hydration path: if Liquid already server-rendered the bar items
    // (the count matches), don't blow them away. Just attach a flyout
    // panel to each. This eliminates the brief swap-flicker on initial
    // paint and preserves the SSR markup search engines see.
    const existing = Array.from(
      this._barList.querySelectorAll(':scope > [data-mh-mm-item]')
    );
    if (existing.length === items.length && existing.length > 0) {
      items.forEach((node, index) => {
        const hasChildren = Array.isArray(node.children) && node.children.length > 0;
        if (!hasChildren) return;
        const li = existing[index];
        if (li.querySelector(':scope > [data-mh-mm-panel]')) return;
        const panelId = `mh-mm-panel-${index + 1}`;
        li.appendChild(this._buildDesktopPanel(node, panelId));
      });
      return;
    }

    // Cold build (no SSR markup, e.g. when JS is loaded into a page that
    // didn't render the snippet — fallback path).
    this._barList.innerHTML = '';

    items.forEach((node, index) => {
      const hasChildren = Array.isArray(node.children) && node.children.length > 0;
      const panelId = `mh-mm-panel-${index + 1}`;

      const li = document.createElement('li');
      li.className = 'mh-megamenu__bar-item';
      li.setAttribute('data-mh-mm-item', '');
      if (hasChildren) li.setAttribute('data-has-children', '');

      const trigger = document.createElement('a');
      trigger.className = 'mh-megamenu__bar-link';
      this._applyButtonStyle(trigger, node, 'mh-megamenu__bar-link');
      trigger.href = node.url || '#';
      trigger.setAttribute('data-mh-mm-trigger', '');
      trigger.setAttribute('data-mh-mm-track', 'menu-link');
      trigger.setAttribute('data-mh-mm-section', 'desktop-bar');
      trigger.setAttribute('data-mh-mm-depth', '1');
      trigger.setAttribute('data-mh-mm-title', node.title);
      if (hasChildren) {
        trigger.setAttribute('aria-haspopup', 'true');
        trigger.setAttribute('aria-expanded', 'false');
        trigger.setAttribute('aria-controls', panelId);
      }

      const label = document.createElement('span');
      label.className = 'mh-megamenu__bar-label';
      label.textContent = node.title;
      trigger.appendChild(label);

      if (node.badge) {
        const badge = document.createElement('span');
        badge.className = 'mh-megamenu__badge';
        badge.textContent = node.badge;
        if (node.badgeColor) badge.style.backgroundColor = node.badgeColor;
        trigger.appendChild(badge);
      }

      if (hasChildren) {
        const caret = document.createElement('span');
        caret.className = 'mh-megamenu__caret';
        caret.setAttribute('aria-hidden', 'true');
        caret.innerHTML =
          '<svg viewBox="0 0 10 6" width="10" height="6" focusable="false"><path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
        trigger.appendChild(caret);
      }

      li.appendChild(trigger);

      if (hasChildren) {
        li.appendChild(this._buildDesktopPanel(node, panelId));
      }

      this._barList.appendChild(li);
    });
  }

  /**
   * Apply opt-in button styling to a rendered top-level item. `node.button`
   * is either `true` (theme defaults from mega-menu.css) or an object with
   * style ("solid" | "outline") and colour overrides, which are forwarded
   * as --mh-mm-btn-* custom properties. No-op when the node isn't a button.
   *
   * @param {HTMLElement} el - the rendered link/button element
   * @param {object} node - the menu tree node
   * @param {string} baseClass - BEM base, e.g. 'mh-megamenu__bar-link'
   */
  _applyButtonStyle(el, node, baseClass) {
    if (!node || !node.button) return;
    const btn = typeof node.button === 'object' ? node.button : {};
    el.classList.add(`${baseClass}--button`);
    if (btn.style === 'outline') el.classList.add(`${baseClass}--button-outline`);
    if (btn.background) el.style.setProperty('--mh-mm-btn-bg', btn.background);
    if (btn.textColor) el.style.setProperty('--mh-mm-btn-text', btn.textColor);
    if (btn.borderColor) el.style.setProperty('--mh-mm-btn-border', btn.borderColor);
    if (btn.hoverBackground) el.style.setProperty('--mh-mm-btn-hover-bg', btn.hoverBackground);
    if (btn.hoverTextColor) el.style.setProperty('--mh-mm-btn-hover-text', btn.hoverTextColor);
    if (btn.borderRadius != null) {
      el.style.setProperty('--mh-mm-btn-radius', `${parseFloat(btn.borderRadius)}px`);
    }
  }

  _buildDesktopPanel(node, panelId) {
    const panel = document.createElement('div');
    panel.id = panelId;
    panel.className = 'mh-megamenu__panel';
    panel.setAttribute('role', 'region');
    panel.setAttribute('aria-label', node.title);
    panel.setAttribute('hidden', '');
    panel.setAttribute('data-mh-mm-panel', '');

    // Optional per-panel background colour from JSON. Accepts any CSS colour
    // string (hex, rgb(), named). Falls back to the global panel_background
    // setting in CSS.
    if (node.panelBackground) {
      panel.style.setProperty('--mh-mm-panel-bg-override', node.panelBackground);
    }

    // Only the empty shell is built up front: it's all the open/close, keyboard and portal wiring
    // needs. The contents (hundreds of elements across all panels) are built the first time a
    // panel is about to be used, so pages where the menu is never opened, and every phone (where
    // the desktop bar is hidden), skip that work.
    this._unbuiltPanelNodes.set(panel, node);
    return panel;
  }

  /**
   * Build a desktop panel's contents if it hasn't been built yet.
   *
   * @param {HTMLElement} panel
   */
  _ensurePanelContent(panel) {
    const node = this._unbuiltPanelNodes.get(panel);
    if (!node) return;
    this._unbuiltPanelNodes.delete(panel);
    const panelId = panel.id;

    const inner = document.createElement('div');
    inner.className = 'mh-megamenu__panel-inner';

    const children = node.children || [];
    // Use vertical-tabs layout when at least one direct child has its own
    // children. A flat list of leaf items (e.g. a seasonal "gift finder")
    // falls back to the simple auto-balancing grid.
    const useTabs = children.some(
      (c) => Array.isArray(c.children) && c.children.length > 0
    );

    if (useTabs) {
      inner.classList.add('mh-megamenu__panel-inner--tabs');
      this._buildPanelTabs(inner, children, panelId);
    } else {
      const grid = document.createElement('div');
      grid.className = 'mh-megamenu__panel-grid';
      children.forEach((child) => {
        grid.appendChild(this._buildDesktopNode(child, 1));
      });
      inner.appendChild(grid);
    }

    if (node.featuredImage) {
      inner.appendChild(this._buildFeature(node, 'mh-megamenu__feature'));
    }

    panel.appendChild(inner);

    // Close button — every desktop flyout gets a dismiss control so the
    // panel can be closed without moving the pointer off it or pressing Esc.
    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'mh-megamenu__panel-close';
    closeBtn.setAttribute('aria-label', 'Close menu');
    closeBtn.innerHTML =
      '<svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false"><path d="M3 3l10 10M13 3L3 13" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';
    closeBtn.addEventListener('click', () => this._closePanel());
    panel.appendChild(closeBtn);
  }

  /**
   * Point a feature image at a resized copy instead of the original upload. Shopify's CDN resizes
   * on the `width` parameter; other hosts get the URL unchanged.
   *
   * @param {HTMLImageElement} img
   * @param {string} url - featuredImage URL from the menu tree
   */
  _setFeatureImageSource(img, url) {
    let source;
    try {
      source = new URL(url, window.location.origin);
    } catch (err) {
      img.src = url;
      return;
    }

    const resizable = source.hostname === 'cdn.shopify.com' || source.pathname.startsWith('/cdn/shop/');
    if (!resizable) {
      img.src = url;
      return;
    }

    // The feature column is about a quarter of the panel and can show the image up to 420px tall
    // with object-fit: cover, so size generously for retina screens.
    const widths = [600, 900, 1200, 1600];
    const sources = (base) => {
      const sized = (width) => {
        base.searchParams.set('width', String(width));
        return base.toString();
      };
      return { srcset: widths.map((width) => `${sized(width)} ${width}w`).join(', '), src: sized(900) };
    };

    const original = sources(new URL(source));
    let preferred = original;

    // Menu images are pasted as cdn.shopify.com file URLs. The store serves the same files from
    // /cdn/shop/files/ on its own domain, which reuses the page's connection instead of opening a
    // new one to cdn.shopify.com. Fall back to the original URL if that ever fails.
    const fileMatch =
      source.hostname === 'cdn.shopify.com' && source.pathname.match(/^\/s\/files\/\d+\/\d+\/\d+\/files\/(.+)$/);
    if (fileMatch) {
      preferred = sources(new URL(`/cdn/shop/files/${fileMatch[1]}${source.search}`, window.location.origin));
      img.addEventListener(
        'error',
        () => {
          img.srcset = original.srcset;
          img.src = original.src;
        },
        { once: true }
      );
    }

    img.sizes = '(min-width: 1100px) 40vw, 100vw';
    img.srcset = preferred.srcset;
    img.src = preferred.src;
  }

  /**
   * Get a panel ready ahead of time: build its contents if needed and start downloading its
   * images. They're lazy so closed panels cost nothing, but images in a panel the shopper is about
   * to open (or in its hidden tabs) should already be on their way when it appears.
   *
   * @param {HTMLElement} panel
   */
  _preparePanel(panel) {
    this._ensurePanelContent(panel);
    panel.querySelectorAll('img[loading="lazy"]').forEach((img) => {
      img.loading = 'eager';
    });
  }

  /**
   * Build a feature image block (used both at the top-level panel edge and
   * inside each per-tab tabpanel).
   *
   * @param {object} node - tree node providing featuredImage / featuredImageLink / url / title
   * @param {string} className - the wrapper element's class (controls placement)
   */
  _buildFeature(node, className) {
    const featUrl = node.featuredImageLink || node.url || '';
    const feature = document.createElement('div');
    feature.className = className;

    const img = document.createElement('img');
    img.className = 'mh-megamenu__feature-image';
    img.alt = node.title;
    // loading must be set before src: assigning src first starts the download immediately, so
    // every hidden panel's image was fetched on every page view (phones included).
    img.loading = 'lazy';
    img.decoding = 'async';
    this._setFeatureImageSource(img, node.featuredImage);

    if (featUrl) {
      const a = document.createElement('a');
      a.className = 'mh-megamenu__feature-link';
      a.href = featUrl;
      a.tabIndex = -1;
      a.setAttribute('data-mh-mm-track', 'menu-link');
      a.setAttribute('data-mh-mm-section', 'desktop-feature');
      a.setAttribute('data-mh-mm-depth', '1');
      a.setAttribute('data-mh-mm-title', node.title);
      a.appendChild(img);
      feature.appendChild(a);
    } else {
      feature.appendChild(img);
    }
    return feature;
  }

  /**
   * Build a vertical tablist + tabpanels inside a desktop flyout.
   * Each direct child of the top-level node becomes a tab; the active
   * tabpanel renders that child's grandchildren as the existing column grid.
   * Tab-switch on hover/focus, navigate on click. First tab is pre-selected.
   */
  _buildPanelTabs(inner, children, panelId) {
    const tabsWrap = document.createElement('div');
    tabsWrap.className = 'mh-megamenu__panel-tabs-wrap';

    const tabs = document.createElement('ul');
    tabs.className = 'mh-megamenu__panel-tabs list-unstyled';
    tabs.setAttribute('role', 'tablist');
    tabs.setAttribute('aria-orientation', 'vertical');

    const panels = document.createElement('div');
    panels.className = 'mh-megamenu__panel-tabpanels';

    const tabEls = [];
    const panelEls = [];

    children.forEach((child, idx) => {
      const tabId = `${panelId}-tab-${idx}`;
      const tabPanelId = `${panelId}-tabpanel-${idx}`;

      const li = document.createElement('li');
      li.setAttribute('role', 'presentation');

      const tab = document.createElement('button');
      tab.type = 'button';
      tab.id = tabId;
      tab.className = 'mh-megamenu__panel-tab';
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', tabPanelId);
      tab.setAttribute('aria-selected', 'false');
      tab.setAttribute('tabindex', '-1');

      const label = document.createElement('span');
      label.className = 'mh-megamenu__panel-tab-label';
      label.textContent = child.title;
      tab.appendChild(label);

      if (child.badge) {
        const badge = document.createElement('span');
        badge.className = 'mh-megamenu__badge mh-megamenu__badge--inline';
        badge.textContent = child.badge;
        if (child.badgeColor) badge.style.backgroundColor = child.badgeColor;
        tab.appendChild(badge);
      }

      // Caret indicates "this opens further". Only show on tabs whose own
      // children have grandchildren — flat leaf tabs don't need it.
      const tabHasGrandchildren =
        Array.isArray(child.children) && child.children.length > 0;
      if (tabHasGrandchildren) {
        const caret = document.createElement('span');
        caret.className = 'mh-megamenu__panel-tab-caret';
        caret.setAttribute('aria-hidden', 'true');
        caret.innerHTML =
          '<svg viewBox="0 0 16 16" width="14" height="14" focusable="false"><path d="M6 4l4 4-4 4" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
        tab.appendChild(caret);
      }

      li.appendChild(tab);
      tabs.appendChild(li);
      tabEls.push(tab);

      const tabPanel = document.createElement('div');
      tabPanel.id = tabPanelId;
      tabPanel.className = 'mh-megamenu__panel-tabpanel';
      tabPanel.setAttribute('role', 'tabpanel');
      tabPanel.setAttribute('aria-labelledby', tabId);
      tabPanel.setAttribute('tabindex', '0');
      tabPanel.hidden = true;

      // Content column (view-all link + grid of grandchildren)
      const content = document.createElement('div');
      content.className = 'mh-megamenu__panel-tabpanel-content';

      if (child.url) {
        const viewAll = document.createElement('a');
        viewAll.className = 'mh-megamenu__panel-view-all';
        viewAll.href = child.url;
        viewAll.setAttribute('data-mh-mm-track', 'menu-link');
        viewAll.setAttribute('data-mh-mm-section', 'desktop-panel');
        viewAll.setAttribute('data-mh-mm-depth', '2');
        viewAll.setAttribute('data-mh-mm-title', child.title);
        viewAll.textContent = `View all ${child.title}`;
        content.appendChild(viewAll);
      }

      const grandchildren = child.children || [];
      if (grandchildren.length > 0) {
        const grid = document.createElement('div');
        grid.className = 'mh-megamenu__panel-grid';
        grandchildren.forEach((gc) => {
          grid.appendChild(this._buildDesktopNode(gc, 1));
        });
        content.appendChild(grid);
      }
      tabPanel.appendChild(content);

      // Optional feature image on the right of this tab's panel
      if (child.featuredImage) {
        tabPanel.classList.add('mh-megamenu__panel-tabpanel--with-feature');
        tabPanel.appendChild(
          this._buildFeature(child, 'mh-megamenu__panel-tabpanel-feature')
        );
      }

      panels.appendChild(tabPanel);
      panelEls.push(tabPanel);
    });

    tabsWrap.appendChild(tabs);
    inner.appendChild(tabsWrap);
    inner.appendChild(panels);

    this._wirePanelTabs(tabEls, panelEls);
  }

  _wirePanelTabs(tabs, panels) {
    if (tabs.length === 0) return;

    const activate = (idx) => {
      tabs.forEach((t, i) => {
        const active = i === idx;
        t.setAttribute('aria-selected', active ? 'true' : 'false');
        t.setAttribute('tabindex', active ? '0' : '-1');
        t.classList.toggle('mh-megamenu__panel-tab--active', active);
      });
      panels.forEach((p, i) => {
        const active = i === idx;
        p.hidden = !active;
        p.classList.toggle('mh-megamenu__panel-tabpanel--active', active);
      });
    };

    activate(0);

    tabs.forEach((tab, idx) => {
      // Click activates the tab. Focus also activates (so keyboard users see
      // the panel update as they tab through). No hover-to-activate, so the
      // panel doesn't churn while the cursor moves through the list.
      tab.addEventListener('click', () => activate(idx));
      tab.addEventListener('focus', () => activate(idx));
      tab.addEventListener('keydown', (e) => {
        const last = tabs.length - 1;
        let next = idx;
        switch (e.key) {
          case 'ArrowDown':
          case 'ArrowRight':
            next = idx === last ? 0 : idx + 1;
            break;
          case 'ArrowUp':
          case 'ArrowLeft':
            next = idx === 0 ? last : idx - 1;
            break;
          case 'Home':
            next = 0;
            break;
          case 'End':
            next = last;
            break;
          default:
            return;
        }
        e.preventDefault();
        tabs[next].focus();
      });
    });
  }

  /**
   * Render one tree node inside a desktop panel.
   *  depth 1 → "column" (group header + sublist of children)
   *  depth 2+ → list item; if it has children, its own nested sublist
   */
  _buildDesktopNode(node, depth) {
    const hasChildren = Array.isArray(node.children) && node.children.length > 0;

    if (depth === 1) {
      const group = document.createElement('div');
      group.className = 'mh-megamenu__group';
      group.setAttribute('data-mh-mm-group', '');

      const header = document.createElement('div');
      header.className = 'mh-megamenu__group-header';

      if (node.url) {
        const a = document.createElement('a');
        a.className = 'mh-megamenu__group-title';
        a.href = node.url;
        a.setAttribute('data-mh-mm-track', 'menu-link');
        a.setAttribute('data-mh-mm-section', 'desktop-panel');
        a.setAttribute('data-mh-mm-depth', '1');
        a.setAttribute('data-mh-mm-title', node.title);
        a.appendChild(document.createTextNode(node.title));
        if (node.badge) {
          const badge = document.createElement('span');
          badge.className = 'mh-megamenu__badge mh-megamenu__badge--inline';
          badge.textContent = node.badge;
          if (node.badgeColor) badge.style.backgroundColor = node.badgeColor;
          a.appendChild(badge);
        }
        header.appendChild(a);
      } else {
        const span = document.createElement('span');
        span.className = 'mh-megamenu__group-title mh-megamenu__group-title--static';
        span.textContent = node.title;
        header.appendChild(span);
      }
      group.appendChild(header);

      if (hasChildren) {
        const ul = document.createElement('ul');
        ul.className = 'mh-megamenu__group-list list-unstyled';
        ul.setAttribute('aria-label', node.title);
        node.children.forEach((child) => {
          const li = document.createElement('li');
          li.setAttribute('role', 'none');
          li.appendChild(this._buildDesktopNode(child, 2));
          ul.appendChild(li);
        });
        group.appendChild(ul);
      }
      return group;
    }

    // depth >= 2 — return a fragment that may have a link + nested sublist
    const frag = document.createDocumentFragment();

    if (node.url) {
      const a = document.createElement('a');
      a.className = `mh-megamenu__link mh-megamenu__link--depth-${depth}`;
      a.href = node.url;
      a.setAttribute('data-mh-mm-track', 'menu-link');
      a.setAttribute('data-mh-mm-section', 'desktop-panel');
      a.setAttribute('data-mh-mm-depth', String(depth));
      a.setAttribute('data-mh-mm-title', node.title);
      a.appendChild(document.createTextNode(node.title));
      if (node.badge) {
        const badge = document.createElement('span');
        badge.className = 'mh-megamenu__badge mh-megamenu__badge--inline';
        badge.textContent = node.badge;
        if (node.badgeColor) badge.style.backgroundColor = node.badgeColor;
        a.appendChild(badge);
      }
      frag.appendChild(a);
    } else {
      const span = document.createElement('span');
      span.className = `mh-megamenu__link mh-megamenu__link--depth-${depth} mh-megamenu__link--static`;
      span.textContent = node.title;
      frag.appendChild(span);
    }

    if (hasChildren) {
      const ul = document.createElement('ul');
      ul.className = `mh-megamenu__sublist mh-megamenu__sublist--depth-${depth} list-unstyled`;
      node.children.forEach((child) => {
        const li = document.createElement('li');
        li.appendChild(this._buildDesktopNode(child, depth + 1));
        ul.appendChild(li);
      });
      frag.appendChild(ul);
    }

    return frag;
  }

  _openPanel(item) {
    if (this._openItem === item) return;
    if (this._openItem) this._setItemOpen(this._openItem, false);
    this._openItem = item;
    this._setItemOpen(item, true);
    this._setBackdropVisible(true);
    this._measureBar();
  }

  _closePanel() {
    if (!this._openItem) return;
    this._setItemOpen(this._openItem, false);
    this._openItem = null;
    this._setBackdropVisible(false);
  }

  _setItemOpen(item, open) {
    const trigger = item.querySelector('[data-mh-mm-trigger]');
    // The panel may have been portalled to <body>, so look it up via the
    // map we cached in _wireDesktop instead of via DOM traversal.
    const panel = (this._panelByItem && this._panelByItem.get(item))
      || item.querySelector('[data-mh-mm-panel]');
    if (!panel) return;
    if (open) {
      item.setAttribute('data-mh-mm-open', '');
      // Includes images in the panel's hidden tabs, so switching tabs shows them straight away.
      this._preparePanel(panel);
      panel.removeAttribute('hidden');
      // Force a layout pass so the transition runs from the closed state
      void panel.offsetWidth;
      requestAnimationFrame(() => panel.setAttribute('data-mh-mm-open', ''));
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
    } else {
      item.removeAttribute('data-mh-mm-open');
      panel.removeAttribute('data-mh-mm-open');
      // Wait for transition to finish before hiding from AT
      setTimeout(() => {
        if (!panel.hasAttribute('data-mh-mm-open')) panel.setAttribute('hidden', '');
      }, this._animationMs());
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    }
  }

  _setBackdropVisible(visible) {
    if (!this._backdrop) return;
    if (visible) {
      this._backdrop.removeAttribute('hidden');
      requestAnimationFrame(() => this._backdrop.setAttribute('data-mh-mm-visible', ''));
    } else {
      this._backdrop.removeAttribute('data-mh-mm-visible');
      setTimeout(() => {
        if (!this._backdrop.hasAttribute('data-mh-mm-visible')) this._backdrop.setAttribute('hidden', '');
      }, this._animationMs());
    }
  }

  _handleDocClick(e) {
    if (!this._openItem) return;
    // The host (bar items, triggers) — clicking these is internal.
    if (this.contains(e.target)) return;
    // Panels are portalled to <body>, so this.contains() doesn't cover them.
    // Treat a click anywhere inside the open panel as internal.
    const openPanel = this._panelByItem && this._panelByItem.get(this._openItem);
    if (openPanel && openPanel.contains(e.target)) return;
    this._closePanel();
  }

  _handleKeydown(e) {
    if (e.key === 'Escape') {
      if (this._openItem) {
        const trigger = this._openItem.querySelector('[data-mh-mm-trigger]');
        this._closePanel();
        if (trigger) trigger.focus();
      } else if (this._drawer && this._drawer.hasAttribute('data-mh-mm-open')) {
        this._closeDrawer();
      }
    }
  }

  _measureBar() {
    // Position the panel's top edge just below the bar
    const rect = this.getBoundingClientRect();
    const bottom = rect.bottom;
    this.style.setProperty('--mh-mm-bar-bottom', bottom + 'px');
  }

  // Must match the bar / drawer breakpoint in mega-menu.css.
  static DESKTOP_MIN_WIDTH = 1100;
  // Below this viewport width (but at or above DESKTOP_MIN_WIDTH) the bar
  // is forced into the centered second-row layout. Above it the bar
  // sits inline next to the logo unless collision detection trips.
  // 1280 covers most "tablet" widths and small windowed browsers while
  // letting standard laptop screens (>=13" full screen) get the inline bar.
  static TABLET_STACK_MAX_WIDTH = 1280;

  /**
   * Set data-menu-style on #header-component. The theme's own
   * setHeaderMenuStyle would do this from HeaderComponent.connectedCallback,
   * but it needs a <header-menu> ref which we don't render in json_tree
   * mode. Set it ourselves based purely on viewport width — `(hover: none)`
   * misfires on touch-capable laptops, and the click-to-toggle bar works
   * fine for touch users regardless.
   */
  _syncHeaderMenuStyle() {
    const header = document.getElementById('header-component');
    if (!header) return;
    const isDrawer = window.innerWidth < MakerHouseMegaMenu.DESKTOP_MIN_WIDTH;
    header.dataset.menuStyle = isDrawer ? 'drawer' : 'menu';
  }

  /* -- Analytics --------------------------------------------------------- */

  /**
   * Selector list covering every clickable link in the desktop bar, the
   * desktop flyout panels, and the mobile drawer. Capture-phase listener
   * catches clicks before navigation so the event fires even when the link
   * has no preventDefault. Noibu auto-tracks DOM clicks; the data-mh-mm-*
   * attributes on each link give it stable identifiers to bucket by.
   */
  _wireAnalytics() {
    document.addEventListener('click', this._handleAnalyticsClick, true);
  }

  _handleAnalyticsClick(e) {
    // Catch every clickable menu link via its rendered class. The
    // base classes — bar-link, group-title, link, feature-link,
    // panel-view-all, drawer-link — match both the top-level link
    // and any --modifier variant on the same element.
    const link = e.target.closest && e.target.closest(
      [
        '.mh-megamenu__bar-link',
        '.mh-megamenu__group-title',
        '.mh-megamenu__link',
        '.mh-megamenu__feature-link',
        '.mh-megamenu__panel-view-all',
        '.mh-megamenu__drawer-link',
      ].join(',')
    );
    if (!link) return;

    // Only fire for real navigational links (not the toggle buttons we use
    // for "this tab has children" disclosure).
    const href = link.getAttribute && link.getAttribute('href');
    if (!href) return;

    const data = {
      menu_item_title: (link.textContent || '').trim(),
      menu_item_url: href,
      menu_section: this._sectionFor(link),
      menu_depth: this._depthFor(link),
    };

    this._dispatchAnalytics('navigation_menu_click', data);

    // Stash the menu hierarchy as a sub-nav context hint so the related-
    // collections block on the destination page can show siblings from
    // the branch the shopper actually clicked through (e.g. Christmas
    // Cards under Holidays vs under Cards). Referrer alone can't carry
    // this — both clicks come from the same source page.
    this._stashSubnavContext(link);
  }

  /**
   * Walk up from the clicked link to find the nearest meaningful "parent"
   * URL in the rendered menu (its group title, the tab panel's view-all
   * link, or the drawer level's view-all link). Store it in
   * sessionStorage keyed by the click's target path. The destination
   * page's sub-nav reads this to disambiguate collections that appear
   * under more than one branch.
   */
  _stashSubnavContext(link) {
    const targetHref = link.getAttribute('href');
    if (!targetHref) return;
    const target = this._normSamePath(targetHref);
    if (!target) return;

    let contextHref = '';

    const group = link.closest && link.closest('.mh-megamenu__group');
    if (group) {
      const title = group.querySelector('a.mh-megamenu__group-title');
      if (title && title !== link) contextHref = title.getAttribute('href') || '';
    }
    if (!contextHref) {
      const panel = link.closest && link.closest('.mh-megamenu__panel-tabpanel');
      if (panel) {
        const viewAll = panel.querySelector('a.mh-megamenu__panel-view-all');
        if (viewAll && viewAll !== link) contextHref = viewAll.getAttribute('href') || '';
      }
    }
    if (!contextHref) {
      const level = link.closest && link.closest('.mh-megamenu__drawer-panel-level');
      if (level) {
        const vAll = level.querySelector('a.mh-megamenu__drawer-link--view-all');
        if (vAll && vAll !== link) contextHref = vAll.getAttribute('href') || '';
      }
    }

    const context = this._normSamePath(contextHref);
    if (!context || context === target) return;

    try {
      sessionStorage.setItem('subnav:branch:' + target, context);
    } catch (e) {
      /* quota / private mode — non-fatal */
    }
  }

  _normSamePath(href) {
    if (!href) return '';
    try {
      const u = new URL(href, location.origin);
      if (u.origin !== location.origin) return '';
      return u.pathname || '';
    } catch (e) {
      return '';
    }
  }

  _sectionFor(link) {
    if (link.closest('.mh-megamenu__drawer')) return 'mobile-drawer';
    if (link.classList.contains('mh-megamenu__bar-link')) return 'desktop-bar';
    if (link.classList.contains('mh-megamenu__feature-link')) return 'desktop-feature';
    return 'desktop-panel';
  }

  _depthFor(link) {
    // Prefer an explicit data-mh-mm-depth (set when we render the link).
    const explicit = link.dataset && link.dataset.mhMmDepth;
    if (explicit) return Number(explicit);
    // Otherwise infer from class names — depth-N is encoded on
    // .mh-megamenu__link--depth-N for panel sub-links.
    const depthMatch = link.className.match(/--depth-(\d+)/);
    if (depthMatch) return Number(depthMatch[1]);
    if (link.classList.contains('mh-megamenu__bar-link')) return 1;
    if (link.classList.contains('mh-megamenu__group-title')) return 1;
    if (link.classList.contains('mh-megamenu__feature-link')) return 1;
    if (link.classList.contains('mh-megamenu__panel-view-all')) return 1;
    if (link.classList.contains('mh-megamenu__drawer-link--view-all')) return 1;
    return 2;
  }

  /**
   * Fire one synthetic event to every analytics consumer that's loaded.
   * Each try/catch is independent so a missing global never breaks the
   * chain. Noibu picks up the underlying DOM click on its own.
   */
  _dispatchAnalytics(eventName, data) {
    // 1. GTM dataLayer — picked up by GTM containers for forwarding.
    try {
      if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        window.dataLayer.push({ event: eventName, ...data });
      }
    } catch (_) {}

    // 2. GA4 direct via gtag.
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'select_content', {
          content_type: 'navigation_menu',
          item_id: data.menu_item_url,
          item_name: data.menu_item_title,
          menu_section: data.menu_section,
          menu_depth: data.menu_depth,
        });
      }
    } catch (_) {}

    // 3. Shopify Analytics — newer Pixel-style API.
    try {
      if (
        window.Shopify &&
        window.Shopify.analytics &&
        typeof window.Shopify.analytics.publish === 'function'
      ) {
        window.Shopify.analytics.publish(eventName, data);
      }
    } catch (_) {}

    // 4. Shopify Analytics — legacy theme path.
    try {
      if (
        window.ShopifyAnalytics &&
        window.ShopifyAnalytics.lib &&
        typeof window.ShopifyAnalytics.lib.track === 'function'
      ) {
        window.ShopifyAnalytics.lib.track('Navigation Menu Click', data);
      }
    } catch (_) {}
  }

  /**
   * If the bar's column is too narrow to hold its items alongside the logo,
   * apply `.mh-megamenu--stacked` on the header columns so the bar drops to
   * a centered second row. Uses asymmetric checks (one direction at a time)
   * to avoid feedback loops as the layout reflows.
   */
  /**
   * Toggle `.mh-megamenu--stacked` on the header columns purely by viewport
   * width. Three bands:
   *
   *   < DESKTOP_MIN_WIDTH        → drawer (bar hidden entirely)
   *   < TABLET_STACK_MAX_WIDTH   → stacked (bar sits below logo on row 2)
   *   otherwise                  → inline (bar sits next to logo on row 1)
   *
   * Collision detection was tried earlier but turned out to be unreliable —
   * the bar's actual position (left / center / right column) means the
   * "barRect.right > logoRect.left" geometry doesn't generalize. Viewport
   * bands give predictable, debuggable behaviour. If a merchant's bar
   * overflows in the inline band, raise TABLET_STACK_MAX_WIDTH.
   */
  _checkHeaderStack() {
    const cols = document.querySelector('.header__columns');
    if (!cols) return;

    const w = window.innerWidth;

    if (w < MakerHouseMegaMenu.DESKTOP_MIN_WIDTH) {
      cols.classList.remove('mh-megamenu--stacked');
      return;
    }

    if (w < MakerHouseMegaMenu.TABLET_STACK_MAX_WIDTH) {
      cols.classList.add('mh-megamenu--stacked');
      return;
    }

    cols.classList.remove('mh-megamenu--stacked');
  }

  _handleResize() {
    this._measureBar();
    this._syncHeaderMenuStyle();
    this._checkHeaderStack();
    // If we cross the breakpoint while a panel is open, close it
    const w = window.innerWidth;
    const min = MakerHouseMegaMenu.DESKTOP_MIN_WIDTH;
    if (w < min && this._openItem) this._closePanel();
    if (w >= min && this._drawer && this._drawer.hasAttribute('data-mh-mm-open')) {
      this._closeDrawer();
    }
  }

  _animationMs() {
    return parseInt(this.dataset.animationMs || '200', 10);
  }

  /* -- Mobile drawer ------------------------------------------------------ */

  _wireMobile() {
    if (!this._mobileTrigger || !this._drawer) return;

    this._mobileTrigger.addEventListener('click', () => this._openDrawer());
    if (this._drawerClose) {
      this._drawerClose.addEventListener('click', () => this._closeDrawer());
    }
    if (this._drawerBackdrop) {
      this._drawerBackdrop.addEventListener('click', () => this._closeDrawer());
    }
  }

  _openDrawer() {
    if (!this._drawer || !this._drawerStack) return;
    this._lastFocusedBeforeDrawer = document.activeElement;

    // Show the chrome immediately; build / fetch the tree, then populate.
    this._drawer.removeAttribute('hidden');
    requestAnimationFrame(() => {
      this._drawer.setAttribute('data-mh-mm-open', '');
    });
    this._mobileTrigger.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    this._drawer.addEventListener('keydown', this._trapFocus);

    this._loadTree().then(() => {
      this._buildMobileDrawer();
      // Move focus into the drawer once the open transition finishes
      setTimeout(() => {
        const first = this._drawerStack.querySelector('a, button');
        if (first) first.focus();
      }, this._animationMs() + 30);
    });
  }

  _closeDrawer() {
    if (!this._drawer || !this._drawer.hasAttribute('data-mh-mm-open')) return;
    this._drawer.removeAttribute('data-mh-mm-open');
    this._mobileTrigger.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    setTimeout(() => {
      if (!this._drawer.hasAttribute('data-mh-mm-open')) {
        this._drawer.setAttribute('hidden', '');
      }
    }, this._animationMs());
    if (this._lastFocusedBeforeDrawer && typeof this._lastFocusedBeforeDrawer.focus === 'function') {
      this._lastFocusedBeforeDrawer.focus();
    }
    this._drawer.removeEventListener('keydown', this._trapFocus);
  }

  _trapFocus = (e) => {
    if (e.key !== 'Tab') return;
    const focusables = this._getDrawerFocusables();
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  _getDrawerFocusables() {
    const sel = 'a[href], button:not([disabled]):not([hidden]), summary';
    return Array.from(this._drawer.querySelectorAll(sel)).filter(
      (el) => el.offsetParent !== null || el === document.activeElement
    );
  }

  /**
   * Resolve the menu tree once for both desktop and mobile.
   *
   * Source priority:
   *   1. Inline JSON (`<script type="application/json" data-mh-mm-tree>`) —
   *      legacy escape hatch for server-side renders. Set in the constructor
   *      from the host element's content if present.
   *   2. External JSON URL (`data-menu-json-url` on this host element).
   *      Default: assets/menu-tree.json. This is the canonical path.
   *
   * Result is cached in memory; the browser's HTTP cache handles repeat
   * page loads.
   */
  _loadTree() {
    if (this._tree) return Promise.resolve(this._tree);
    if (this._treePromise) return this._treePromise;

    const url = this.dataset.menuJsonUrl;
    if (!url) {
      // No external source and no inline tree — render an empty drawer.
      this._tree = { items: [] };
      return Promise.resolve(this._tree);
    }

    this._treePromise = fetch(url, { credentials: 'same-origin' })
      .then((r) => {
        if (!r.ok) throw new Error(`menu fetch failed: ${r.status}`);
        return r.json();
      })
      .then((data) => {
        this._tree = data && data.items ? data : { items: [] };
        return this._tree;
      })
      .catch((err) => {
        console.warn('[mh-megamenu] failed to load menu tree', err);
        this._tree = { items: [] };
        return this._tree;
      });

    return this._treePromise;
  }

  _buildMobileDrawer() {
    if (!this._tree) return;

    // Reopening reuses the levels already built and just returns to the top level.
    if (this._rootPanelEntry) {
      this._panelStack.slice(1).forEach((p) => p.element.remove());
      this._panelStack = [this._rootPanelEntry];

      const root = this._rootPanelEntry.element;
      root.removeAttribute('data-mh-mm-behind');
      root.setAttribute('data-mh-mm-active', '');
      root.scrollTop = 0;
      this._setDrawerTitle(this._rootPanelEntry.title);
      return;
    }

    this._drawerStack.innerHTML = '';
    this._panelStack = [];
    this._setDrawerTitle(this._defaultDrawerTitle);
    this._pushPanel(this._tree.items || [], this._defaultDrawerTitle, null);
    this._rootPanelEntry = this._panelStack[0];
  }

  /**
   * Push a new slide-in panel onto the stack. Each panel is the children of
   * the parent it was opened from. Panels above the root get a "Back" button
   * and a "View all <parent>" entry at the top when the parent has its own URL.
   */
  _pushPanel(items, title, parentNode) {
    const cachedPanel = parentNode && this._panelCache.get(parentNode);
    const panel = cachedPanel || this._createPanel(items, title, parentNode);

    if (cachedPanel) {
      // A level built on an earlier drill-in: reset it to how a fresh one starts.
      panel.removeAttribute('data-mh-mm-active');
      panel.removeAttribute('data-mh-mm-behind');
      panel.scrollTop = 0;
    } else if (parentNode) {
      this._panelCache.set(parentNode, panel);
    }

    this._drawerStack.appendChild(panel);

    // Mark previous panel as behind, slide this one in
    this._panelStack.forEach((p) => {
      p.element.setAttribute('data-mh-mm-behind', '');
      p.element.removeAttribute('data-mh-mm-active');
    });
    requestAnimationFrame(() => panel.setAttribute('data-mh-mm-active', ''));

    this._panelStack.push({ element: panel, title });
    this._setDrawerTitle(title);

    setTimeout(() => {
      const first = panel.querySelector('a, button');
      if (first) first.focus();
    }, this._animationMs() + 30);
  }

  /**
   * Build the DOM for one drawer level: the children of the node it was opened from.
   */
  _createPanel(items, title, parentNode) {
    const panel = document.createElement('div');
    panel.className = 'mh-megamenu__drawer-panel-level';
    panel.setAttribute('role', 'group');
    panel.setAttribute('aria-label', title);

    // Header row inside the panel: optional Back, optional title.
    if (this._panelStack.length > 0) {
      const header = document.createElement('div');
      header.className = 'mh-megamenu__drawer-level-header';

      const back = document.createElement('button');
      back.type = 'button';
      back.className = 'mh-megamenu__drawer-back';
      back.innerHTML =
        '<span class="mh-megamenu__drawer-back-icon" aria-hidden="true"><svg viewBox="0 0 10 6" width="10" height="6" focusable="false"><path d="M9 5L5 1 1 5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" transform="rotate(-90 5 3)"/></svg></span>' +
        '<span>Back</span>';
      back.addEventListener('click', () => this._popPanel());
      header.appendChild(back);

      // No per-level title here: the drawer's top header already shows the
      // current level's title (see _setDrawerTitle), so repeating it next to
      // the Back button is redundant and confusing.

      panel.appendChild(header);
    }

    const list = document.createElement('ul');
    list.className = 'mh-megamenu__drawer-list list-unstyled';

    if (parentNode && parentNode.url) {
      list.appendChild(this._renderViewAll(parentNode));
    }

    // Button styling only applies at the root level — that's where the
    // JSON's top-level items (the only ones that can carry `button`) live.
    const isRoot = parentNode === null;
    items.forEach((node) => list.appendChild(this._renderItem(node, isRoot)));

    panel.appendChild(list);
    return panel;
  }

  _popPanel() {
    if (this._panelStack.length <= 1) return;
    const top = this._panelStack.pop();
    top.element.removeAttribute('data-mh-mm-active');
    // Detach after the slide-out unless the shopper has already drilled back into this level.
    setTimeout(() => {
      if (!this._panelStack.some((p) => p.element === top.element)) top.element.remove();
    }, this._animationMs());

    const newTop = this._panelStack[this._panelStack.length - 1];
    newTop.element.removeAttribute('data-mh-mm-behind');
    newTop.element.setAttribute('data-mh-mm-active', '');
    this._setDrawerTitle(newTop.title);

    setTimeout(() => {
      const first = newTop.element.querySelector('a, button');
      if (first) first.focus();
    }, this._animationMs() + 30);
  }

  _setDrawerTitle(title) {
    if (this._drawerTitle) this._drawerTitle.textContent = title;
  }

  _renderViewAll(node) {
    const li = document.createElement('li');
    li.className = 'mh-megamenu__drawer-list-item mh-megamenu__drawer-list-item--view-all';
    const a = document.createElement('a');
    a.className = 'mh-megamenu__drawer-link mh-megamenu__drawer-link--view-all';
    a.href = node.url;
    a.setAttribute('data-mh-mm-track', 'menu-link');
    a.setAttribute('data-mh-mm-section', 'mobile-drawer');
    a.setAttribute('data-mh-mm-title', node.title);
    a.textContent = `View all ${node.title}`;
    li.appendChild(a);
    return li;
  }

  _renderItem(node, isRoot) {
    const li = document.createElement('li');
    li.className = 'mh-megamenu__drawer-list-item';

    const hasChildren = Array.isArray(node.children) && node.children.length > 0;

    if (hasChildren) {
      // Drill-down trigger: button that pushes a new panel.
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'mh-megamenu__drawer-link mh-megamenu__drawer-link--parent';
      if (isRoot) this._applyButtonStyle(btn, node, 'mh-megamenu__drawer-link');
      btn.setAttribute('aria-haspopup', 'true');

      btn.appendChild(this._renderLabel(node));

      const chevron = document.createElement('span');
      chevron.className = 'mh-megamenu__drawer-link-chevron';
      chevron.setAttribute('aria-hidden', 'true');
      chevron.innerHTML =
        '<svg viewBox="0 0 16 16" width="14" height="14" focusable="false"><path d="M6 4l4 4-4 4" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      btn.appendChild(chevron);

      btn.addEventListener('click', () => this._pushPanel(node.children, node.title, node));
      li.appendChild(btn);
    } else {
      const a = document.createElement('a');
      a.className = 'mh-megamenu__drawer-link';
      if (isRoot) this._applyButtonStyle(a, node, 'mh-megamenu__drawer-link');
      a.href = node.url || '#';
      a.setAttribute('data-mh-mm-track', 'menu-link');
      a.setAttribute('data-mh-mm-section', 'mobile-drawer');
      a.setAttribute('data-mh-mm-title', node.title);
      a.appendChild(this._renderLabel(node));
      li.appendChild(a);
    }

    return li;
  }

  /**
   * Build the inner content of a link or drill-down button: the title, plus
   * an optional badge.
   */
  _renderLabel(node) {
    const wrap = document.createElement('span');
    wrap.className = 'mh-megamenu__drawer-link-label';

    const text = document.createElement('span');
    text.className = 'mh-megamenu__drawer-link-text';
    text.textContent = node.title;
    wrap.appendChild(text);

    if (node.badge) {
      const badge = document.createElement('span');
      badge.className = 'mh-megamenu__badge mh-megamenu__badge--inline';
      badge.textContent = node.badge;
      if (node.badgeColor) badge.style.backgroundColor = node.badgeColor;
      wrap.appendChild(badge);
    }

    return wrap;
  }
}

if (!customElements.get('mh-megamenu')) {
  customElements.define('mh-megamenu', MakerHouseMegaMenu);
}
